import { Axios as AxiosObservable } from "axios-observable"; // Import axios-observable for observable-based requests
import axios, { AxiosRequestConfig, AxiosInstance } from "axios"; // Import standard axios for promise-based requests
import { serverUrlBase } from "../config/GlobalAppConfig";
import { NotificationType } from "../models/enums";

export enum ApiCallingMethods {
    get = "GET",
    delete = "DELETE",
    head = "HEAD",
    options = "OPTIONS",
    post = "POST",
    put = "PUT",
    patch = "PATCH",
    link = "LINK",
    unlink = "UNLINK",
}

export const HTTP_STATUS_UNAUTHORIZED = 401;

class CustomAxios {
    instance: AxiosObservable;
    callBack: ((params: any, type: NotificationType) => void) | null;
    private accessToken = "";

    setAccessToken(accessToken: string) {
        this.accessToken = accessToken;
    }

    constructor(axiosConfig: AxiosRequestConfig) {
        // Create a standard axios instance first
        const axiosInstance = axios.create(axiosConfig);
        // Then wrap it with AxiosObservable
        this.instance = new AxiosObservable(axiosInstance);
        this.callBack = null;

        // Request interceptor
        this.instance.interceptors.request.use(
            (config: any) => {
                // Initialize headers if undefined (Axios 1.x compatibility)
                if (!config.headers) {
                    config.headers = {};
                }

                const token = this.accessToken;
                if (token) {
                    config.headers["Authorization"] = `Bearer ${token}`;
                }

                if (config.data && typeof config.data.append === 'function') {
                    // FormData: let browser set Content-Type with boundary
                    config.headers.set("Content-Type", undefined);
                } else {
                    if (axiosConfig.responseType) {
                        config.headers.set("Content-Type", axiosConfig.responseType.valueOf());
                    } else {
                        config.headers.set("Content-Type", "application/json");
                    }
                }

                return config;
            },
            (error: any) => Promise.reject(error)
        );

        // Response interceptor
        this.instance.interceptors.response.use(
            (response) => {
                if (this.callBack) this.callBack(response, NotificationType.success);
                return response;
            },
            (error) => {
                if (this.callBack) this.callBack(error, NotificationType.error);
                if (error.response) {
                    const { status } = error.response;
                    if (status === HTTP_STATUS_UNAUTHORIZED) {
                        if (!window.location.href.includes("/Error")) {
                            window.location.href = `/Error?detail=${HTTP_STATUS_UNAUTHORIZED}`;
                        }
                    }
                }
                return Promise.reject(error.response);
            }
        );
    }

    // Observable request using axios-observable (observable-based)
    request = (options: AxiosRequestConfig, callBack: ((resp: any, type: NotificationType) => void) | null = null) => {
        this.callBack = callBack;
        return this.instance.request(options);
    };

    // Using standard axios (promise-based)
    requestAsPromise = async (options: AxiosRequestConfig): Promise<any> => {
        // Create axios instance with baseURL
        const axiosInstance: AxiosInstance = axios.create({
            baseURL: serverUrlBase,
        });

        const mergedOptions = {
            ...options,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${this.accessToken}`,
            },
        };

        try {
            // Returns a promise from standard axios
            return await axiosInstance.request(mergedOptions);
        } catch (error) {
            console.error("Error making request:", error);

            // Provide more detailed feedback
            if (axios.isAxiosError(error)) {
                console.error("Axios error details:", error.response);
                return error.response;
            }

            // Minimal format for error (swagger)
            return {
                message: null,
                didError: true,
                errorMessage: "Network error or server unavailable",
                model: null,
            };
        }
    };
}

const axiosInstance = new CustomAxios({ baseURL: serverUrlBase });
export default axiosInstance;
